/**
 * DataTables reads each cell through the same accessor for several purposes, telling them apart by
 * the `type` argument ("display", "sort", "filter", "type"). A cell that always returns the rendered
 * text is ordered as text, so "10,53" is compared character by character against "2,55" and lands
 * first. These helpers return the raw value when DataTables is ordering, and the formatted text when
 * it is drawing.
 */

/** DataTables asks for the underlying value under these types. */
const ORDERING_TYPES = ["sort", "type"];

/**
 * Builds a cell accessor that orders by a number while displaying formatted text.
 * @param pick Value the column is ordered by. Null/undefined sinks to the bottom of an ascending sort
 * @param format Text rendered in the cell
 */
export const sortByValue = <TRow>(
  pick: (row: TRow) => number | null | undefined,
  format: (row: TRow) => string
) => (
    (row: TRow, type?: string): number | string => (
      type !== undefined && ORDERING_TYPES.includes(type)
        ? pick(row) ?? Number.NEGATIVE_INFINITY
        : format(row)
    )
  );

/**
 * Builds a cell accessor that orders and searches by plain text while drawing rich markup.
 * @param pick Text the column is ordered and searched by
 * @param format Markup rendered in the cell
 */
export const sortByText = <TRow>(
  pick: (row: TRow) => string,
  format: (row: TRow) => string
) => (
    (row: TRow, type?: string): string => (type === "display" ? format(row) : pick(row))
  );

/**
 * Width every run of digits is padded to inside an ordering key. Twelve covers any fleet code or
 * plate a customer registers; a longer run is left as it is, and only loses its place against a
 * shorter one, which no real identifier reaches.
 */
const NATURAL_DIGIT_WIDTH = 12;

/**
 * Turns an identifier into a key that orders naturally under a plain string comparison.
 *
 * DataTables compares the values itself — an accessor cannot hand it a comparator — so the natural
 * order has to be baked into the value. Padding every run of digits makes "2" answer
 * "000000000002" and "10" answer "000000000010", which compare in the right order as text and still
 * as numbers if DataTables decides the column is numeric. Accents are stripped and the case is
 * dropped so "Á" and "a" land beside "A" instead of after "Z".
 *
 * @param text The identifier as the customer registered it
 * @returns Its ordering key
 */
export const naturalOrderKey = (text: string): string => text
  .trim()
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "")
  .toLowerCase()
  .replace(/\d+/g, (digits) => digits.padStart(NATURAL_DIGIT_WIDTH, "0"));

/**
 * Builds a cell accessor for an identifier that mixes text and numbers — a fleet code, a plate.
 *
 * Ordering it as plain text lists "10" before "2", which is what a fleet numbered by hand reads on
 * opening the screen. This orders by the natural key, searches by the identifier the customer
 * typed, and draws the markup.
 *
 * @param pick The identifier the column is ordered and searched by
 * @param format Markup rendered in the cell
 */
export const sortByNatural = <TRow>(
  pick: (row: TRow) => string,
  format: (row: TRow) => string
) => (
    (row: TRow, type?: string): string => {
      if (type === "display") return format(row);

      return type !== undefined && ORDERING_TYPES.includes(type) ? naturalOrderKey(pick(row)) : pick(row);
    }
  );

export default sortByValue;
